import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, DIMENSION_META } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Clock, CheckCircle2, PenLine } from "lucide-react";

export default function LessonDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [l, setL] = useState(null);
  const [reflection, setReflection] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/lessons/${id}`).then((r) => {
      setL(r.data);
      setReflection(r.data.completion?.reflection || "");
    });
  }, [id]);

  if (!l) return <div className="p-16 text-center text-slate-500">Loading…</div>;
  const meta = DIMENSION_META[l.dimension];

  const complete = async () => {
    setSaving(true);
    try {
      await api.post(`/lessons/${id}/complete`, { reflection });
      toast.success(l.completion ? "Reflection updated" : "Lesson completed");
      const r = await api.get(`/lessons/${id}`);
      setL(r.data);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 fade-up">
      <button onClick={() => nav("/app/lessons")} data-testid="lesson-back" className="text-sm text-slate-500 hover:text-[#0B1E36] mb-6 inline-flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back to library
      </button>

      <div className="flex items-center gap-3 text-xs mb-3">
        <span className="uppercase tracking-widest font-semibold" style={{ color: meta.color }}>{meta.label}</span>
        <span className="text-slate-300">•</span>
        <span className="inline-flex items-center gap-1 text-slate-500"><Clock className="h-3 w-3" /> {l.duration_min} min</span>
      </div>
      <h1 className="font-serif text-4xl text-[#0B1E36] leading-tight mb-4">{l.title}</h1>
      <p className="text-lg text-slate-600 leading-relaxed mb-8">{l.summary}</p>

      <img src={l.cover} alt={l.title} className="w-full h-64 object-cover rounded-2xl mb-8" />

      <div className="prose prose-slate max-w-none">
        <p className="text-[#0B1E36] leading-relaxed text-lg" data-testid="lesson-content">{l.content}</p>
      </div>

      <div className="mt-10 bg-[#C9A961]/10 border border-[#C9A961]/30 p-8 rounded-2xl">
        <div className="text-xs uppercase tracking-widest text-[#6B5228] mb-2">Practice prompt</div>
        <div className="font-serif text-xl text-[#0B1E36] leading-snug">{l.practice_prompt}</div>
      </div>

      <div className="mt-8 bg-white p-8 rounded-2xl border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <PenLine className="h-4 w-4 text-[#EC4899]" />
          <div className="text-sm font-semibold text-[#0B1E36]">Reflection journal</div>
        </div>
        <textarea
          data-testid="lesson-reflection" value={reflection} onChange={(e) => setReflection(e.target.value)}
          rows={5} placeholder="What surfaced for you? What will you try?"
          className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none resize-none" />
        <div className="mt-4 flex items-center justify-between">
          {l.completion ? (
            <div className="text-xs text-[#2C7A7B] font-semibold inline-flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" /> Completed
            </div>
          ) : <div />}
          <button
            onClick={complete} disabled={saving} data-testid="lesson-complete"
            className="bg-[#0B1E36] text-white px-6 py-2.5 rounded-full font-semibold btn-lift hover:bg-[#132a4a] disabled:opacity-60">
            {saving ? "Saving…" : l.completion ? "Update reflection" : "Mark complete"}
          </button>
        </div>
      </div>
    </div>
  );
}
